<template>
  <div id="app">
    <section
      class="filterBanner"
      :class="[{ moreFilterActive: toggleFiltersInView }]"
    >
      <div class="container">
        <RenderResources
          :indexName="searchIndex"
          :endPoint="apidomain"
          :filteryear="filteryear"
          :parentid="parentid"
          :ocpApimSubscriptionKey="ocpApimSubscriptionKey"
          :customHeaders="customHeaders"
          :page="1"
          :filtersRequired="1"
        >
          <template slot-scope="{ result, loading, error }">
            <div class="formContainer">
              <div class="error" v-if="error">
                {{ $t("LoadingError") }}
              </div>
              <div class="loading" v-if="loading && !error">
                <p>{{ $t("LoadingFilters") }}</p>
              </div>
              <form @submit.prevent="updateKeyword" v-if="!loading && !error">
                <div class="sortingFilters">
                  <div class="searchInput">
                    <label for="searchBannerInput" class="sr-only">{{
                      $t("SearchLabel")
                    }}</label>
                    <input
                      id="searchBannerInput"
                      @keydown.enter.prevent="updateKeyword"
                      type="text"
                      :placeholder="$t('SearchLabel')"
                      class="sortingSearchInput"
                      v-model="keyword"
                    />
                    <button
                      class="clearButton"
                      v-if="keyword.length > 0"
                      @click="clearKeyword"
                      :aria-label="$t('ClearKeyword')"
                    >
                      <svg
                        aria-hidden="true"
                        role="presentation"
                        id="close"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 14 13"
                      >
                        <g
                          id="Projects"
                          stroke="none"
                          stroke-width="1"
                          fill="none"
                          fill-rule="evenodd"
                        >
                          <g
                            id="DHI.Desktop.Project.Index.1a"
                            transform="translate(-706.000000, -394.000000)"
                            fill="#000000"
                          >
                            <path
                              d="M714,392.686292 L714,399.685292 L721,399.686292 L721,401.686292 L714,401.686292 L714,408.686292 L712,408.686292 L712,401.686292 L705,401.686292 L705,399.686292 L712,399.686292 L712,392.686292 L714,392.686292 Z"
                              id="icon/close"
                              transform="translate(713.000000, 400.686292) rotate(45.000000) translate(-713.000000, -400.686292) "
                            ></path>
                          </g>
                        </g>
                      </svg>
                    </button>
                    <button
                      type="submit"
                      class="searchButton"
                      @click="updateKeyword"
                      :aria-label="$t('SubmitSearch')"
                    >
                      <svg
                        aria-hidden="true"
                        role="presentation"
                        id="resourceSearchIcon"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 18 18"
                      >
                        <path
                          d="M17.875 16.46l-4-4.005c-.13-.132-.274-.25-.43-.35l-1-.69c2.057-2.549 2.076-6.184.046-8.754C10.461.091 6.924-.727 3.973.69 1.023 2.107-.554 5.382.178 8.575c.731 3.194 3.575 5.454 6.847 5.442 1.588 0 3.13-.54 4.37-1.532l.75 1.002c.089.129.19.25.3.36l4 4.005c.094.095.222.148.355.148.133 0 .261-.053.355-.148l.7-.7c.19-.189.199-.493.02-.692zm-10.85-4.445c-2.761 0-5-2.241-5-5.006s2.239-5.006 5-5.006 5 2.241 5 5.006c0 1.328-.527 2.6-1.464 3.54-.938.938-2.21 1.466-3.536 1.466z"
                        />
                      </svg>
                    </button>
                  </div>

                  <Filters
                    :tagGroups="result.facets"
                    :showYearFilter="filteryear"
                    @change="updateTags"
                    @changeDate="updateYearTags"
                    @toggleFilters="switchFilterOverlay"
                    :tags="tags"
                    :datetags="datetags"
                    :filtergroupids="filtergroupids.split(',')"
                    class="filterWrap"
                  ></Filters>
                </div>
              </form>
            </div>
          </template>
        </RenderResources>
      </div>
    </section>
    <section class="indexSearchResults">
      <div class="container">
        <RenderResources
          :parentid="parentid"
          :indexName="searchIndex"
          :endPoint="apidomain"
          :tags="selectedTags"
          :ocpApimSubscriptionKey="ocpApimSubscriptionKey"
          :customHeaders="customHeaders"
          :years="selectedYears"
          :page="currentPage"
          :keyword="keywordInput"
          :pagesize="pagesize"
          :sortorder="[orderBy]"
          @change="resourcesChanged"
        >
          <template v-slot="{ result, loading, error }">
            <div class="inner">
              <div class="resultsTop">
                <ResultsIndicator
                  v-if="
                    selectedTags.length > 0 ||
                    selectedYears.length > 0 ||
                    result.count > 0
                  "
                  :first="firstPage"
                  :term="keywordInput"
                  :last="lastPage(result.count)"
                  :total="result.count"
                  :indexTitle="title"
                ></ResultsIndicator>
                <div class="chosenResults">
                  <ChosenTags :tags="tags" v-on:clearTag="clear"></ChosenTags>
                  <ChosenDateTags
                    :tags="datetags"
                    v-on:clearTag="clearDateTag"
                  ></ChosenDateTags>
                  <button
                    class="clearAll"
                    v-if="tags.length > 0"
                    v-on:click="clearAll"
                  >
                    {{ $t("ClearAll") }}
                  </button>
                </div>
              </div>
              <div class="error" v-if="error">
                {{ $t("LoadingResourceError") }}
              </div>
              <div class="loading" v-if="loading && !error">
                <p>{{ $t("LoadingResults") }}</p>
              </div>
              <div
                class="resultsContainer"
                v-if="
                  !loading &&
                  !error &&
                  result.documents !== undefined &&
                  result.documents.length > 0
                "
              >
                <component
                  :is="resultType"
                  v-for="responseValueItem in result.documents"
                  :key="responseValueItem.id"
                  :result="responseValueItem"
                ></component>
              </div>

              <div
                class="noResults"
                v-if="
                  !loading &&
                  !error &&
                  (result === undefined || result.count === 0)
                "
              >
                <p>{{ $t("NoResults") }}</p>
              </div>
            </div>
          </template>
        </RenderResources>

        <div class="pagination" v-if="totalPages > 1">
          <paginate
            :page-count="totalPages"
            :click-handler="paginationClick"
            :prev-text="$t('Previous')"
            :next-text="$t('Next')"
            :container-class="'className'"
          ></paginate>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import Paginate from "vuejs-paginate";

import Vue from "vue";
import ChosenTags from "./components/ChosenTags.vue";
import ChosenDateTags from "./components/ChosenDateTags.vue";
import Filters from "./components/Filters.vue";
import ResultsIndicator from "./components/ResultsIndicator.vue";
import ResultResource from "./components/ResultResource.vue";
import ResultProject from "./components/ResultProject.vue";
import RenderResources from "./components/RenderResources.vue";
import { AzureSearchResponse, TaxonomyTag, GenericSearchResponseFacetsCreatedItem, GenericSearchRequestTaxonomyItem } from "./client/src/models/index"

export default Vue.extend({
  name: "SearchIndex",
  props: {
    showkeywords: {
      type: Boolean,
      required: true,
      default: true
    },
    filtergroupids: {
      type: String,
      required: true,
      default: ""
    },
    filteryear: {
      type: Boolean,
      required: true,
      default: true
    },
    parentid: {
      type: Number,
      required: true,
      default: -1
    },
    pagesize: {
      type: Number,
      required: true,
      default: 20
    },
    title: {
      type: String,
      required: true,
      default: ""
    },
    resultType: {
      type: String,
      required: true,
      default: ""
    },
    ocpApimSubscriptionKey: {
      type: String,
      required: false,
      default: ""
    },
    apidomain: {
      type: String,
      required: false,
      default: ""
    },
    searchIndex: {
      type: String,
      required: false,
      default: ""
    }
  },
  data() {
    return {
      keyword: "" as string,
      keywordInput: "" as string,
      currentPage: 1 as number,
      sortOptions: ["A-Z", "Z-A"] as string[],
      customHeaders: {"Accept": "application/json;v=1"} as Record<string, any>,
      sortOption: "A-Z" as string,
      selectedTags: [],
      selectedYears: [],
      orderBy: "created desc" as string,
      totalPages: Number,
      tags: [] as TaxonomyTag[],
      datetags: [] as GenericSearchResponseFacetsCreatedItem[],
      toggleFiltersInView: false as boolean,
    };
  },
  computed: {
    firstPage(): number {
      return (this.currentPage - 1) * this.pagesize + 1;
    },
  },
  methods: {
    /*Tag methods*/
    clearAll() {
      this.tags = [];
      this.datetags = [];
      this.updateClear();
    },
    clear(tag: TaxonomyTag) {
      this.tags.splice(this.tags.indexOf(tag), 1);
      this.updateTags(this.tags);
    },
    clearDateTag(tag: GenericSearchResponseFacetsCreatedItem) {
      this.datetags.splice(this.datetags.indexOf(tag), 1);
      this.updateYearTags(this.datetags);
    },
    /******/
    switchFilterOverlay(on: boolean) {
      this.toggleFiltersInView = on;
    },
    lastPage(total: number): number {
      return this.currentPage * this.pagesize > total
        ? total
        : this.currentPage * this.pagesize;
    },
    calculateTotalPages(total: number): number {
      return Math.ceil(total / this.pagesize);
    },
    paginationClick(page: number) {
      window.scrollTo(0, 0);
      const elmnt = document.getElementById("resources");
      if (elmnt) {
        elmnt.scrollIntoView();
      }

      this.$data.currentPage = page;
    },
    convertTagtoSearchTag(
      tag: TaxonomyTag
    ): GenericSearchRequestTaxonomyItem {
      const response: GenericSearchRequestTaxonomyItem = {
        tagId: tag.id,
        categoryId: tag.categoryId,
      };
      return response;
    },
    updateTags(tags: TaxonomyTag[]) {
      this.$data.currentPage = 1;
      this.$data.selectedTags = tags.map((x) => this.convertTagtoSearchTag(x));
    },
    updateClear() {
      this.$data.currentPage = 1;
      this.$data.selectedTags = [];
      this.$data.selectedYears = [];
    },
    updateYearTags(
      tags: GenericSearchResponseFacetsCreatedItem[]
    ) {
      this.$data.currentPage = 1;
      this.$data.selectedYears = tags.map((x) => x.value?.getFullYear());
    },
    updateYearClear() {
      this.$data.keyword = "";
      this.$data.keywordInput = "";
      this.$data.currentPage = 1;
      this.$data.selectedYears = [];
    },
    updateKeyword() {
      this.$data.keywordInput = this.$data.keyword;
    },
    clearKeyword() {
      this.$data.keywordInput = "";
      this.$data.keyword = "";
    },
    resourcesChanged(update: AzureSearchResponse) {
      const total = update.count !== undefined ? update.count : 0;
      this.$data.totalPages = this.calculateTotalPages(total);
    },
  },

  components: {
    Filters,
    ResultsIndicator,
    ResultProject,
    ResultResource,
    Paginate,
    RenderResources,
    ChosenTags,
    ChosenDateTags
  },
});
</script>
